import { Badge } from "@/components/Badge.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { WTooltip } from "@/components/WTooltip.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { ContentLoadingSuspense } from "@/components/loading/Loading.tsx";
import { DeleteModal } from "@/components/modal/DeleteModal.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableName } from "@/components/table/DetailsTableName.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { getResourceLink } from "@/modules/api/resourceTypes.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { allInterfacesQueryAtom } from "@/modules/compute/vmInterfacesQuery.ts";
import { LocationDetail } from "@/modules/location/LocationDetail.tsx";
import { ipQueryAtom } from "@/modules/network/ipAddress/ipAddressApi.ts";
import { getAddressFromResource, getDisplayName } from "@/modules/network/ipAddress/resourceId.ts";
import { lbQueryAtom } from "@/modules/network/loadbalancer/loadBalancerApi.ts";
import { vpcVmTargetsAtom } from "@/modules/network/vpc/members.ts";
import {
    deleteVpcNetworkMutation,
    modifyVpcNetworkMutation,
    setDefaultVpcNetworkMutation,
    vpcQueryAtom,
    type VpcWithType,
} from "@/modules/network/vpc/vpcApi.ts";
import { ResourceStatusBadge } from "@/modules/resource/ResourceStatusBadge.tsx";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { useQueryResultAtom, useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { VpcUpdateBody } from "@warrenio/api-spec/spec.oats.gen";
import { filterNulls } from "@warrenio/utils/collections/filterNulls";
import { mapFromEntries } from "@warrenio/utils/collections/maps";
import { uniqueBy } from "remeda";

export function VpcView({ location, vpcId }: { location: string; vpcId: string }) {
    //#region Hooks
    const { t } = useLingui();
    const deleteMutation = useStandardMutation(deleteVpcNetworkMutation);
    const setDefaultMutation = useStandardMutation(setDefaultVpcNetworkMutation);
    const modifyMutation = useStandardMutation(modifyVpcNetworkMutation);

    const data = useSuspenseQueryAtom(vpcQueryAtom(location));

    const vpc = useDeletableResource(() => getResourceById(data, vpcId, "vpc"), deleteMutation);
    //#endregion

    if (vpc === undefined) {
        return <NavigateAfterDelete />;
    }

    // NB: We do not use `resources_count` on purpose here, as it can be inaccurate!
    const { $type, name, subnet, created_at, uuid, subnet_ipv6, is_default } = vpc;

    async function onDelete() {
        await deleteMutation.mutateAsync({ location, uuid: vpcId });
    }

    async function setDefault() {
        await setDefaultMutation.mutateAsync({ location, uuid: vpcId });
    }

    async function onModify({ name: newName }: VpcUpdateBody) {
        newName = newName.trim();
        if (newName && name !== newName) {
            await modifyMutation.mutateAsync({
                body: { name: newName },
                location,
                uuid,
            });
        }
    }

    return (
        <>
            <Toolbar>
                <WButton
                    color="primary"
                    size="bar"
                    variant="ghost"
                    action={async () => await setDefault()}
                    isDisabled={is_default}
                    icon="jp-icon-checkmark"
                >
                    <Trans>Set default</Trans>
                </WButton>

                <DeleteModal isDisabled={is_default} modalAction={async () => await onDelete()}>
                    <Trans>Deleting the VPC network "{name}" will remove it from your account.</Trans>
                </DeleteModal>
            </Toolbar>

            <ContentPane className="VStack gap-4">
                <ViewTitle title={name ?? ""}>
                    {is_default && (
                        <span className="inline-flex items-center font-size-small pl-4">
                            <Badge inHeader noDot>
                                <MaskIcon className="jp-icon-checkmark size-0.75rem" />
                            </Badge>
                            <span className="pl-1">
                                <Trans>Default</Trans>
                            </span>
                        </span>
                    )}
                </ViewTitle>

                <DetailsHolder>
                    <DetailsTable>
                        <DetailsTableName
                            value={name ?? ""}
                            isRequired
                            onChange={async (name) => await onModify({ name })}
                        />
                        <DetailsTableRow title={t`Type`}>
                            <ResourceWithIcon type={$type} />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`IPv4 range`}>
                            <ClipBoardTooltip>{subnet}</ClipBoardTooltip>
                        </DetailsTableRow>
                        <DetailsTableRow title={t`IPv6 range`}>
                            {!!subnet_ipv6 && <ClipBoardTooltip>{subnet_ipv6}</ClipBoardTooltip>}
                        </DetailsTableRow>
                    </DetailsTable>

                    <Spacer />

                    <DetailsTable>
                        <DetailsTableRow title={t`UUID`}>
                            <ClipBoardTooltip text={uuid}>{uuid}</ClipBoardTooltip>
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Created`}>
                            <LongDate date={created_at} />
                        </DetailsTableRow>
                        <LocationDetail slug={location} />
                    </DetailsTable>
                </DetailsHolder>
            </ContentPane>

            <Separator />

            <ContentPane className="VStack gap-4">
                <SectionHeader title={<Trans>Resources</Trans>} />
                <ContentLoadingSuspense>
                    <ResourcesTable obj={vpc} />
                </ContentLoadingSuspense>
            </ContentPane>
        </>
    );
}

function ResourcesTable({ obj }: { obj: VpcWithType }) {
    const { t } = useLingui();
    const { location, uuid } = obj;
    const data = useSuspenseQueryAtom(ipQueryAtom(location));

    const { data: vms } = useQueryResultAtom(vpcVmTargetsAtom(location));
    const balancers = useSuspenseQueryAtom(lbQueryAtom(location));
    const interfaces = useSuspenseQueryAtom(allInterfacesQueryAtom(location));

    const vpcInterfaces = interfaces.filter((item) => item.network.uuid === uuid);
    const vmUuidsFromInterfaces = vpcInterfaces.map((item) => item.vm_uuid).filter((uuid) => !!uuid);
    const vmsFromInterfaces = filterNulls(vmUuidsFromInterfaces.map((uuid) => vms?.get(uuid)));

    // TODO: Move this "index" into the query result so it can be used elsewhere?
    const assignedToIps = mapFromEntries(data.values(), (ip) => [ip.assigned_to, ip]);

    const vmResources = filterNulls(obj.vm_uuids.map((uuid) => vms?.get(uuid)));

    const allVmResources = uniqueBy([...vmResources, ...vmsFromInterfaces], (r) => r.uuid);

    const balancerResources = [...balancers.values()].filter((b) => b.network_uuid === obj.uuid);

    const items = [...allVmResources, ...balancerResources];

    return (
        <WTable>
            <thead>
                <tr>
                    <th>
                        <Trans>Name</Trans>
                    </th>
                    <th>
                        <Trans>Type</Trans>
                    </th>
                    <th>
                        <Trans>Public IPv6</Trans>
                    </th>
                    <th>
                        <Trans>Public IPv4</Trans>
                    </th>
                    <th>
                        <Trans>Private IPv4</Trans>
                    </th>
                    <th>
                        <Trans>Status</Trans>
                    </th>
                    <th>
                        <Trans>Last modified</Trans>
                    </th>
                </tr>
            </thead>
            <WTableBody>
                {items.map((item) => {
                    const { $type } = item;
                    const ipAddress = assignedToIps.get(item.uuid);

                    const action = getResourceLink(item);

                    const address = ipAddress?.address;
                    const publicIpv6 = $type === "virtual_machine" ? item.public_ipv6 : "";
                    const privateIpv4 = getAddressFromResource(item);

                    return (
                        <tr key={item.uuid}>
                            <td>
                                <WTooltip text={t`View`}>
                                    <WButton color="primary" size="xs" variant="ghost" action={action}>
                                        {getDisplayName(item)}
                                    </WButton>
                                </WTooltip>
                            </td>
                            <td>
                                <ResourceWithIcon type={item.$type} />
                            </td>
                            <td>{publicIpv6 ? <ClipBoardTooltip>{publicIpv6}</ClipBoardTooltip> : "-"}</td>
                            <td>{address ? <ClipBoardTooltip>{address}</ClipBoardTooltip> : "-"}</td>
                            <td>{privateIpv4 ? <ClipBoardTooltip>{privateIpv4}</ClipBoardTooltip> : "-"}</td>
                            <td>
                                {item.$type === "virtual_machine" || item.$type === "managed_service" ? (
                                    <ResourceStatusBadge item={item} />
                                ) : null}
                            </td>
                            <td>{item.updated_at ? <LongDate date={item.updated_at} /> : null}</td>
                        </tr>
                    );
                })}
            </WTableBody>
        </WTable>
    );
}
